import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getHitThreshold, scaledCoordinate } from '../drawing.helper'

export type CrossLineDrawingState = {
    p1?: Point
}

export type CrossLineDrawingSettings = {
    lineType: LineTypes,
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point
}

class CrossLineDrawingTool extends DrawingBase<any, CrossLineDrawingSettings, CrossLineDrawingState> {
    scaledCoordinates = {
        p1x: 0,
        p1y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let verticalStart = this.getVerticalStartCoordinate()
        let verticalEnd = this.getVerticalEndCoordinate()
        if (!verticalStart || !verticalEnd || verticalStart.x === 0) return

        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(verticalStart.x, verticalStart.y)
        ctx.lineTo(verticalEnd.x, verticalEnd.y)
        ctx.stroke()
        ctx.closePath()

        let horizontalStart = this.getHorizontalStartCoordinate()
        let horizontalEnd = this.getHorizontalEndCoordinate()
        if (!horizontalStart || !horizontalEnd || horizontalStart.y === 0) return

        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(horizontalStart.x, horizontalStart.y)
        ctx.lineTo(horizontalEnd.x, horizontalEnd.y)
        ctx.stroke()
        ctx.closePath()
    };

    getHorizontalStartCoordinate(): PointCoordinates | void {
        const { p1y } = this.scaledCoordinates
        return {
            x: 0,
            y: p1y
        }
    }

    getHorizontalEndCoordinate(): PointCoordinates | void {
        let { p1y } = this.scaledCoordinates
        return { x: this.canvasWidth, y: p1y }
    }

    calculateLineAttributes() {
        const { p1 } = this._state
        if (!p1) return
        let p1x = this.timeToCoordinate(p1.x)
        let p1y = this.priceToCoordinate(p1.y)
        if (p1x && p1y) {
            p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates = { p1x, p1y }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(lineWidth * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            lineDash = [lineWidth, lineWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    getVerticalStartCoordinate(): PointCoordinates | void {
        const { p1x } = this.scaledCoordinates
        return {
            x: p1x,
            y: 0
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1 } = this._state
        if (!p1) return []
        const min = Math.min(p1.y, p1.y)
        const max = Math.max(p1.y, p1.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1 } = this._state
        if (!p1) return []

        const p1x = this.timeToCoordinate(p1.x)
        if (p1x === undefined || p1x === null) return []

        return [{
            id: 'time',
            p1: p1.x,
            p2: p1.x,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getVerticalEndCoordinate(): PointCoordinates | void {
        let { p1x } = this.scaledCoordinates
        return { x: p1x, y: this.canvasHeight }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!p1) {
            this._state.p1 = point
            !preview && this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1 } = this._state
        if (p1) {
            handles.push(p1)
        }
        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const verticalLineStart = this.getVerticalStartCoordinate()
        const verticalLineEnd = this.getVerticalEndCoordinate()
        if (verticalLineStart && verticalLineEnd) {
            const verticalLineX = verticalLineStart.x
            const xPoint = verticalLineX ?? 0
            const threshold = getHitThreshold(this.horizontalPixelRatio)
            if (scaledX >= xPoint - threshold && scaledX <= xPoint + threshold) {
                return {
                    externalId: this.id,
                    zOrder: 'normal'
                }
            }
        }
        const horizontalLineStart = this.getHorizontalStartCoordinate()
        const horizontalLineEnd = this.getHorizontalEndCoordinate()
        if (horizontalLineStart && horizontalLineEnd) {
            const horizontalLineY = horizontalLineStart.y
            const yPoint = horizontalLineY ?? 0
            const threshold = getHitThreshold(this.verticalPixelRatio)
            if (scaledY >= yPoint - threshold && scaledY <= yPoint + threshold) {
                return {
                    externalId: this.id,
                    zOrder: 'normal'
                }
            }
        }
        return null
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1 } = this._state
        if (!p1) return
        const p1x = p1.x
        switch (id) {
            case 'p1':
                p1.x = this.options.drawingType === 'horizontalLine' ? p1x : time
                p1.y = price
                break
        }
    }

    onSettingsUpdate = (settings: Partial<CrossLineDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<CrossLineDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#2962ff',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                }
            ]
        }]
    }

    getSettingsValues(): CrossLineDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        return settings
    }

    getDrawingType(): DrawingTypes {
        return 'crossLine'
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof CrossLineDrawingSettings)[]
    }
}

export default CrossLineDrawingTool